/**
 * Sección «Proyectos» de la configuración (solo administradores): alta, editar (nombre y enlace al
 * repo), activar o desactivar y eliminar proyectos. La API es quien exige el permiso; aquí solo se oculta.
 *
 * Un proyecto con tareas no se puede eliminar (409 `proyecto_en_uso`): se desactiva, y sigue visible
 * en sus tareas pero ya no se ofrece en las nuevas.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import {
  enlaceRepoSchema,
  nombreProyectoSchema,
  type ActualizarProyectoEntrada,
  type Proyecto,
} from '@cdtec-backlog/contrato';
import { useAvisos } from '../../componentes/Avisos';
import { ErrorDeApi, mensajeDeError } from '../../lib/api';
import { proyectoExistente } from './api';
import { EnlaceRepo } from './EnlaceRepo';
import {
  useActualizarProyecto,
  useCrearProyecto,
  useEliminarProyecto,
  useProyectos,
} from './hooks';
import './SeccionProyectos.css';

type Validado<T> = { valor: T } | { error: string; campo: 'nombre' | 'enlace' };

function validar(nombre: string, enlace: string): Validado<{ nombre: string; enlace: string }> {
  const analisisNombre = nombreProyectoSchema.safeParse(nombre);
  if (!analisisNombre.success) {
    return {
      error: analisisNombre.error.issues[0]?.message ?? 'Nombre inválido.',
      campo: 'nombre',
    };
  }
  const analisisEnlace = enlaceRepoSchema.safeParse(enlace);
  if (!analisisEnlace.success) {
    return {
      error: analisisEnlace.error.issues[0]?.message ?? 'Enlace inválido.',
      campo: 'enlace',
    };
  }
  return { valor: { nombre: analisisNombre.data, enlace: analisisEnlace.data } };
}

function FilaProyecto({ proyecto }: { proyecto: Proyecto }) {
  const actualizar = useActualizarProyecto();
  const eliminar = useEliminarProyecto();
  const { mostrar } = useAvisos();
  const [editando, setEditando] = useState(false);
  const [nombre, setNombre] = useState(proyecto.nombre);
  const [enlace, setEnlace] = useState(proyecto.enlaceRepo ?? '');
  const [confirmando, setConfirmando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const campo = useRef<HTMLInputElement>(null);
  const campoEnlace = useRef<HTMLInputElement>(null);
  const botonEditar = useRef<HTMLButtonElement>(null);
  const botonEliminar = useRef<HTMLButtonElement>(null);
  const ocupado = actualizar.isPending || eliminar.isPending;

  useEffect(() => {
    if (editando) campo.current?.focus();
  }, [editando]);

  const cerrarEdicion = () => {
    setEditando(false);
    setError(null);
    setTimeout(() => botonEditar.current?.focus(), 0);
  };

  const guardar = async (evento: FormEvent) => {
    evento.preventDefault();
    const resultado = validar(nombre, enlace);
    if ('error' in resultado) {
      setError(resultado.error);
      (resultado.campo === 'nombre' ? campo : campoEnlace).current?.focus();
      return;
    }
    // Solo se manda lo que cambió; el enlace vacío se manda como `null` (quita el enlace).
    const cambios: ActualizarProyectoEntrada = {};
    if (resultado.valor.nombre !== proyecto.nombre) cambios.nombre = resultado.valor.nombre;
    const nuevoEnlace = resultado.valor.enlace === '' ? null : resultado.valor.enlace;
    if (nuevoEnlace !== proyecto.enlaceRepo) cambios.enlaceRepo = nuevoEnlace;
    if (Object.keys(cambios).length === 0) {
      cerrarEdicion();
      return;
    }
    try {
      await actualizar.mutateAsync({ id: proyecto.id, cambios });
      mostrar(`Proyecto «${resultado.valor.nombre}» actualizado.`);
      cerrarEdicion();
    } catch (falla) {
      const existente = proyectoExistente(falla);
      setError(
        existente
          ? `Ya existe un proyecto llamado «${existente.nombre}».`
          : falla instanceof ErrorDeApi
            ? mensajeDeError(falla)
            : 'No se pudo guardar.',
      );
      campo.current?.focus();
    }
  };

  const cambiarActivo = async () => {
    setError(null);
    try {
      await actualizar.mutateAsync({ id: proyecto.id, cambios: { activo: !proyecto.activo } });
      mostrar(
        proyecto.activo ? `«${proyecto.nombre}» desactivado.` : `«${proyecto.nombre}» activado.`,
      );
    } catch (falla) {
      setError(mensajeDeError(falla));
    }
  };

  const borrar = async () => {
    setError(null);
    try {
      await eliminar.mutateAsync(proyecto.id);
      mostrar(`«${proyecto.nombre}» eliminado.`);
    } catch (falla) {
      setConfirmando(false);
      setError(
        falla instanceof ErrorDeApi && falla.codigo === 'proyecto_en_uso'
          ? `«${proyecto.nombre}» tiene tareas, desactívalo en su lugar.`
          : mensajeDeError(falla),
      );
      setTimeout(() => botonEliminar.current?.focus(), 0);
    }
  };

  const idError = `error-proyecto-${proyecto.id}`;
  return (
    <li className="proyectos__fila">
      {editando ? (
        <form className="proyectos__edicion" onSubmit={(e) => void guardar(e)} noValidate>
          <div className="proyectos__campos">
            <label htmlFor={`proyecto-nombre-${proyecto.id}`} className="solo-lectores">
              Nuevo nombre de {proyecto.nombre}
            </label>
            <input
              id={`proyecto-nombre-${proyecto.id}`}
              ref={campo}
              type="text"
              value={nombre}
              maxLength={100}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? idError : undefined}
              onChange={(e) => setNombre(e.target.value)}
              onKeyDown={(e) => e.key === 'Escape' && cerrarEdicion()}
            />
            <label htmlFor={`proyecto-enlace-${proyecto.id}`} className="solo-lectores">
              Enlace al repo de {proyecto.nombre} (opcional)
            </label>
            <input
              id={`proyecto-enlace-${proyecto.id}`}
              ref={campoEnlace}
              type="text"
              inputMode="url"
              value={enlace}
              maxLength={500}
              placeholder="Enlace al repo (opcional): https://…"
              onChange={(e) => setEnlace(e.target.value)}
              onKeyDown={(e) => e.key === 'Escape' && cerrarEdicion()}
            />
          </div>
          <button type="submit" className="boton boton--primario" disabled={ocupado}>
            Guardar
          </button>
          <button type="button" className="boton" onClick={cerrarEdicion}>
            Cancelar
          </button>
        </form>
      ) : (
        <>
          <span className="proyectos__nombre" title={proyecto.nombre}>
            {proyecto.nombre}
          </span>
          <EnlaceRepo proyecto={proyecto} />
          {!proyecto.activo && <span className="etiqueta">Inactivo</span>}
          {confirmando ? (
            <span className="proyectos__acciones">
              <button
                type="button"
                className="boton boton--peligro"
                disabled={ocupado}
                onClick={() => void borrar()}
              >
                Sí, eliminar
              </button>
              <button
                type="button"
                className="boton"
                onClick={() => {
                  setConfirmando(false);
                  setTimeout(() => botonEliminar.current?.focus(), 0);
                }}
              >
                No eliminar
              </button>
            </span>
          ) : (
            <span className="proyectos__acciones">
              <button
                type="button"
                className="boton"
                ref={botonEditar}
                aria-label={`Editar ${proyecto.nombre}`}
                disabled={ocupado}
                onClick={() => {
                  setNombre(proyecto.nombre);
                  setEnlace(proyecto.enlaceRepo ?? '');
                  setError(null);
                  setEditando(true);
                }}
              >
                Editar
              </button>
              <button
                type="button"
                className="boton"
                aria-label={`${proyecto.activo ? 'Desactivar' : 'Activar'} ${proyecto.nombre}`}
                disabled={ocupado}
                onClick={() => void cambiarActivo()}
              >
                {proyecto.activo ? 'Desactivar' : 'Activar'}
              </button>
              <button
                type="button"
                className="boton boton--peligro"
                ref={botonEliminar}
                aria-label={`Eliminar ${proyecto.nombre}`}
                disabled={ocupado}
                onClick={() => {
                  setError(null);
                  setConfirmando(true);
                }}
              >
                Eliminar
              </button>
            </span>
          )}
        </>
      )}
      {error && (
        <p id={idError} role="alert" className="proyectos__error">
          {error}
        </p>
      )}
    </li>
  );
}

export function SeccionProyectos() {
  const proyectos = useProyectos();
  const crear = useCrearProyecto();
  const { mostrar } = useAvisos();
  const [nombre, setNombre] = useState('');
  const [enlace, setEnlace] = useState('');
  const [error, setError] = useState<string | null>(null);
  const campo = useRef<HTMLInputElement>(null);
  const campoEnlace = useRef<HTMLInputElement>(null);

  const agregar = async (evento: FormEvent) => {
    evento.preventDefault();
    const resultado = validar(nombre, enlace);
    if ('error' in resultado) {
      setError(resultado.error);
      (resultado.campo === 'nombre' ? campo : campoEnlace).current?.focus();
      return;
    }
    setError(null);
    try {
      await crear.mutateAsync({
        nombre: resultado.valor.nombre,
        ...(resultado.valor.enlace ? { enlaceRepo: resultado.valor.enlace } : {}),
      });
      mostrar(`Proyecto «${resultado.valor.nombre}» agregado.`);
      setNombre('');
      setEnlace('');
    } catch (falla) {
      const existente = proyectoExistente(falla);
      setError(
        existente
          ? `Ya existe «${existente.nombre}»${existente.activo ? '.' : ' (está inactivo, puedes activarlo en la lista).'}`
          : mensajeDeError(falla),
      );
      campo.current?.focus();
    }
  };

  return (
    <section className="seccion-config proyectos" aria-labelledby="titulo-proyectos">
      <h2 id="titulo-proyectos">Proyectos</h2>
      <p>
        Los proyectos que se pueden elegir en una tarea. Cada uno puede tener un enlace a su repo.
        Un proyecto con tareas no se puede eliminar: desactívalo y deja de ofrecerse en las tareas
        nuevas, pero se sigue viendo en las que ya lo tienen.
      </p>
      {proyectos.isError && <p role="alert">{mensajeDeError(proyectos.error)}</p>}
      {proyectos.isPending && <p>Cargando proyectos…</p>}
      {proyectos.data && proyectos.data.length === 0 && <p>Aún no hay proyectos.</p>}
      {proyectos.data && proyectos.data.length > 0 && (
        <ul className="proyectos__lista" aria-label="Proyectos">
          {proyectos.data.map((proyecto) => (
            <FilaProyecto key={proyecto.id} proyecto={proyecto} />
          ))}
        </ul>
      )}
      <form className="proyectos__alta" onSubmit={(e) => void agregar(e)} noValidate>
        <div className="campo">
          <label htmlFor="proyecto-alta">Nuevo proyecto</label>
          <input
            id="proyecto-alta"
            ref={campo}
            type="text"
            value={nombre}
            maxLength={100}
            placeholder="cdtec-backlog o Infraestructura GCP"
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? 'proyecto-alta-error' : undefined}
            onChange={(e) => setNombre(e.target.value)}
          />
        </div>
        <div className="campo">
          <label htmlFor="proyecto-alta-enlace">Enlace al repo (opcional)</label>
          <input
            id="proyecto-alta-enlace"
            ref={campoEnlace}
            type="text"
            inputMode="url"
            value={enlace}
            maxLength={500}
            placeholder="https://…"
            onChange={(e) => setEnlace(e.target.value)}
          />
        </div>
        <button type="submit" className="boton boton--primario" disabled={crear.isPending}>
          Agregar
        </button>
        {error && (
          <p id="proyecto-alta-error" role="alert" className="proyectos__error">
            {error}
          </p>
        )}
      </form>
    </section>
  );
}
